/**
 * COB2B-809 — sales-rep "Welcome Back!" pop up: Select a user + Select your location.
 *
 * Sales reps only: it needs #location-switcher-data, which layout/theme.liquid renders
 * only for a rep in a company.
 *
 *  1. Users: members of the session's company. Same endpoint, label and rep-email filter
 *     as the cart's "Order details" contact dropdown (draft_cart.js getCustomerData), but
 *     without a locationId, so every member is listed.
 *  2. Locations: only those the selected user holds a role on (backend).
 *  3. Confirm: the backend grants the rep a role on that location and writes
 *     custom.current_location_id. The chosen user is saved into cartState so the cart's
 *     contact dropdown opens on them. Then reload: #location-switch-marker renders and
 *     reconcileCartCompany() does the native rebind, the cart clear and the role prune —
 *     nothing here repeats that.
 *
 * Opening:
 *  - Automatically once per login. The "seen" flag lives in localStorage keyed by
 *    customer id (sessionStorage is per tab). It's cleared by an inline script on the
 *    logged-out login portal (snippets/login-portal.liquid), or here on a post-login
 *    `shop_sign_in` landing. Never while a switch is pending: the page would navigate
 *    away under it.
 *  - On demand from any `[data-open-location-switcher]` element. None exist yet; a
 *    future trigger is markup only and always opens, seen or not.
 */
import API_ENDPOINTS from '../config/api.js';
import { proxyFetch } from '../objects/proxyAuth.js';
import { mountOverlay, closeModal } from './addToListLogin';

const LOG = '[location_switcher]';
// Also cleared by name in snippets/login-portal.liquid — keep the two in step.
const SEEN_KEY = 'ceres-location-popup-seen';
const TRIGGER = '[data-open-location-switcher]';
const LOCATION_GID_PREFIX = 'gid://shopify/CompanyLocation/';

// Cart fields scoped to a company/location — dropped when the scope changes, exactly
// as draft_cart.js dropOutOfScopeDraftFields() does (COB2B-798 #1).
const SCOPED_CART_FIELDS = ['poNumber', 'deliveryDate', 'note', 'orderNote', 'discountCodes'];

function readData() {
  const el = document.getElementById('location-switcher-data');
  if (!el) return null;
  try {
    return JSON.parse(el.textContent);
  } catch (e) {
    console.error(LOG, 'unreadable #location-switcher-data', e);
    return null;
  }
}

function escapeHtml(value) {
  const div = document.createElement('div');
  div.textContent = value == null ? '' : String(value);
  return div.innerHTML;
}

async function postJson(url, body) {
  const res = await proxyFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
  return json.data;
}

/* -------------------------------------------------------------------------- */
/* Once-per-login flag                                                        */
/* -------------------------------------------------------------------------- */

function readSeen() {
  try {
    return localStorage.getItem(SEEN_KEY);
  } catch (e) {
    return null;
  }
}

function writeSeen(customerId) {
  try {
    localStorage.setItem(SEEN_KEY, customerId);
  } catch (e) {
    // Storage blocked: the pop up just shows again on the next page.
  }
}

function clearSeen() {
  try {
    localStorage.removeItem(SEEN_KEY);
  } catch (e) {
    // Nothing to clear.
  }
}

// The main reset is an inline script in snippets/login-portal.liquid: every logged-out
// render clears the flag. This bundle never gets this far on that page, because an earlier
// global init throws there. This is the backup for a login that lands with
// `shop_sign_in` in the URL. A reload of that URL (e.g. after Confirm) must not count as
// a fresh login. Shopify's own scripts may read the param, so it's left in place.
function resetSeenOnSignInLanding() {
  const navigation = performance.getEntriesByType?.('navigation')?.[0];
  const isReload = navigation?.type === 'reload';
  const justSignedIn =
    !isReload && new URLSearchParams(window.location.search).has('shop_sign_in');

  if (justSignedIn) clearSeen();
}

function shouldAutoOpen(data) {
  if (readSeen() === data.customerId) return false;
  // A pending native switch navigates away straight after load; open on the landing.
  if (document.getElementById('location-switch-marker')) return false;
  return true;
}

/* -------------------------------------------------------------------------- */
/* Cart hand-off                                                              */
/* -------------------------------------------------------------------------- */

function readCartState() {
  try {
    return JSON.parse(localStorage.getItem('cartState')) || {};
  } catch (e) {
    return {};
  }
}

function cartScope(data, locationId) {
  return {
    companyId: data.currentCompanyId,
    locationId: `${LOCATION_GID_PREFIX}${locationId}`,
  };
}

// The cart's cached contact, if it was picked under the current company + location.
function cachedContactId(data) {
  const state = readCartState();
  const scope = cartScope(data, data.currentLocationId);
  const inScope = state.companyId === scope.companyId && state.locationId === scope.locationId;
  return inScope ? state.selectedContactId : null;
}

// Save the chosen user where the cart's contact dropdown restores from. A new location
// is a new scope, so the other scoped fields go — same rule the cart applies on load.
function rememberContactForCart(data, contactId, locationId) {
  const state = readCartState();
  const scope = cartScope(data, locationId);
  const sameScope = state.companyId === scope.companyId && state.locationId === scope.locationId;

  if (!sameScope) SCOPED_CART_FIELDS.forEach((field) => { state[field] = null; });
  Object.assign(state, scope, { selectedContactId: contactId, companyContactId: contactId });

  try {
    localStorage.setItem('cartState', JSON.stringify(state));
  } catch (e) {
    console.error(LOG, 'could not save the contact for the cart', e);
  }
}

/* -------------------------------------------------------------------------- */
/* Pop up                                                                     */
/* -------------------------------------------------------------------------- */

// Same source + filter as the cart's contact dropdown: the rep is a member of the
// company too, but never someone they order for.
async function fetchUsers(data) {
  const result = await postJson(API_ENDPOINTS.COMPANY_CONTACTS);
  const repEmail = (data.customerEmail || '').toLowerCase();
  return (result.contacts?.edges || [])
    .map((edge) => edge.node)
    .filter((node) => {
      const email = node?.customer?.email;
      return !email || email.toLowerCase() !== repEmail;
    })
    .map(({ id, customer: { email, firstName, lastName } }) => ({
      id,
      label: firstName || lastName ? `${firstName || ''} ${lastName || ''} (${email})`.trim() : email,
    }));
}

function optionsHtml(items, selectedId, labelKey) {
  return items
    .map(
      (item) =>
        `<option value="${escapeHtml(item.id)}"${item.id === selectedId ? ' selected' : ''}>${escapeHtml(item[labelKey])}</option>`,
    )
    .join('');
}

const PLACEHOLDER = '<option value="">Please select</option>';

function selectField(id, label) {
  return `
    <div class="mr-location-modal-field">
      <label class="mr-location-modal-label" for="${id}">${label}</label>
      <div class="mr-location-modal-select-wrap">
        <select class="mr-location-modal-select" id="${id}" disabled>${PLACEHOLDER}</select>
      </div>
    </div>`;
}

// Returns true when the modal mounted (another overlay may already be open).
function renderModal(data, users) {
  const overlay = mountOverlay(
    'mr-login-modal-overlay',
    `
    <div class="mr-login-modal mr-location-modal" role="dialog" aria-modal="true" aria-labelledby="mr-location-modal-title">
      <button type="button" class="mr-login-modal-close" aria-label="Close">
        <svg viewBox="0 0 14 14" width="14" height="14" fill="none" aria-hidden="true">
          <path d="M1 1 L13 13 M13 1 L1 13" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
        </svg>
      </button>
      <div class="mr-login-modal-title" id="mr-location-modal-title">Welcome Back!</div>
      ${selectField('mr-location-modal-user', 'Select a user')}
      ${selectField('mr-location-modal-location', 'Select your location')}
      <p class="mr-location-modal-message" role="status" hidden></p>
      <div class="mr-login-modal-actions">
        <button type="button" class="mr-login-modal-login" data-location-confirm disabled>Confirm</button>
      </div>
    </div>`,
  );
  if (!overlay) return false;

  const userSelect = overlay.querySelector('#mr-location-modal-user');
  const locationSelect = overlay.querySelector('#mr-location-modal-location');
  const confirm = overlay.querySelector('[data-location-confirm]');
  const message = overlay.querySelector('.mr-location-modal-message');

  const showMessage = (text) => {
    message.textContent = text || '';
    message.hidden = !text;
  };
  const syncConfirm = () => {
    confirm.disabled = !userSelect.value || !locationSelect.value;
  };

  overlay.querySelector('.mr-login-modal-close').addEventListener('click', closeModal);

  if (!users.length) {
    showMessage('There are no other users in this company.');
    return true;
  }

  // Auto-select a lone user, like the cart; otherwise restore the cart's in-scope pick.
  const cached = cachedContactId(data);
  const preselected =
    users.length === 1 ? users[0].id : users.find((u) => u.id === cached)?.id || '';
  userSelect.innerHTML = PLACEHOLDER + optionsHtml(users, preselected, 'label');
  userSelect.disabled = false;

  let request = 0;
  const loadLocations = async () => {
    const contactId = userSelect.value;
    const current = ++request;
    locationSelect.innerHTML = PLACEHOLDER;
    locationSelect.disabled = true;
    showMessage('');
    syncConfirm();
    if (!contactId) return;

    try {
      const result = await postJson(API_ENDPOINTS.SALES_REP_LOCATIONS, { contactId });
      if (current !== request) return; // a newer user was picked meanwhile

      const locations = result.locations || [];
      if (!locations.length) {
        showMessage("This user isn't assigned to any location.");
        return;
      }
      const selected =
        locations.length === 1
          ? locations[0].id
          : locations.find((loc) => loc.id === result.currentLocationId)?.id || '';
      locationSelect.innerHTML = PLACEHOLDER + optionsHtml(locations, selected, 'name');
      locationSelect.disabled = false;
      syncConfirm();
    } catch (e) {
      if (current !== request) return;
      console.error(LOG, 'could not load locations', e);
      showMessage("Couldn't load this user's locations. Please try again.");
    }
  };

  userSelect.addEventListener('change', loadLocations);
  locationSelect.addEventListener('change', syncConfirm);

  confirm.addEventListener('click', async () => {
    const contactId = userSelect.value;
    const locationId = locationSelect.value;
    if (!contactId || !locationId) return;

    // Already there: nothing to switch, just hand the user to the cart.
    if (locationId === data.currentLocationId) {
      rememberContactForCart(data, contactId, locationId);
      closeModal();
      return;
    }

    confirm.disabled = true;
    showMessage('');
    try {
      await postJson(API_ENDPOINTS.SALES_REP_SET_LOCATION, { contactId, locationId });
      rememberContactForCart(data, contactId, locationId);
      window.location.reload();
    } catch (e) {
      console.error(LOG, 'switch failed', e);
      showMessage("Couldn't change the location. Please try again.");
      syncConfirm();
    }
  });

  if (userSelect.value) loadLocations();
  return true;
}

/**
 * The single way to open the pop up. `auto` applies the once-per-login rule; a trigger
 * (manual open) always opens.
 */
export async function openLocationSwitcher({ auto = false } = {}) {
  const data = readData();
  if (!data) return;
  if (auto && !shouldAutoOpen(data)) return;

  try {
    const users = await fetchUsers(data);
    // Re-check: a trigger may have opened it while the users were loading.
    if (auto && readSeen() === data.customerId) return;
    if (renderModal(data, users)) writeSeen(data.customerId);
  } catch (e) {
    // Not marked as seen, so it tries again on the next page.
    console.error(LOG, 'could not load users', e);
  }
}

export default function locationSwitcher() {
  resetSeenOnSignInLanding();
  if (!document.getElementById('location-switcher-data')) return;

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest(TRIGGER);
    if (!trigger) return;
    e.preventDefault();
    openLocationSwitcher();
  });

  openLocationSwitcher({ auto: true });
}
